import { useCreate, useNotify, useRefresh, useRecordContext, useGetList } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete
} from '@mui/material';
import { useState } from 'react';
import { useOpportunityStages } from '../../opportunities/useOpportunityStages.ts';

const AddOpportunityQuick = () => {
  const [open, setOpen] = useState(false);
  const [programServiceIds, setProgramServiceIds] = useState<number[]>([]);
  const [notes, setNotes] = useState('');
  const notify = useNotify();
  const refresh = useRefresh();
  const contact = useRecordContext();
  const [create, { isLoading }] = useCreate();

  // Every product across every program, so one opportunity can mix them.
  const { data: services = [] } = useGetList('program-services', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'name', order: 'ASC' }
  });

  const { firstStageId } = useOpportunityStages();

  const handleSave = () => {
    if (!contact?.id || programServiceIds.length === 0) {
      notify('Pick at least one product', { type: 'warning' });
      return;
    }

    create(
      'opportunities',
      {
        data: {
          contactId: contact.id,
          status: 'OPEN',
          opportunityStageId: firstStageId,
          programServiceIds,
          dateOpened: new Date().toISOString(),
          notes
        }
      },
      {
        onSuccess: () => {
          notify('Opportunity created', { type: 'success' });
          refresh();
          setOpen(false);
          setProgramServiceIds([]);
          setNotes('');
        },
        onError: (error) => {
          notify(
            error instanceof Error ? `Error: ${error.message}` : 'An unexpected error occurred',
            { type: 'error' }
          );
        }
      }
    );
  };

  return (
    <>
      <Button variant="contained" color="primary" size="small" onClick={() => setOpen(true)}>
        Add Opportunity
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Quick Add Opportunity</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={services}
                groupBy={(option) => option.program?.name ?? 'Other'}
                getOptionLabel={(option) => option.name}
                value={services.filter((s) => programServiceIds.includes(s.id))}
                onChange={(_, value) => {
                  setProgramServiceIds(value.map((s) => s.id));
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Products"
                    margin="normal"
                    fullWidth
                    placeholder="Search across all programs"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || programServiceIds.length === 0}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddOpportunityQuick;
